// File: src/pages/EditTeamRolePosition.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Button, Input, Label, Spinner } from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import swal from "sweetalert";

import { _Get, _Put } from "../import/ApiEndPoint/ApiCalling";
import {
  ViewDepartmentWise_Assign_Role,
  Update_AssignRole_InDepartment1,
} from "../import/ApiEndPoint/Api";

const injectOnce = () => {
  if (document.getElementById("edit-h-inline-css")) return;
  const s = document.createElement("style");
  s.id = "edit-h-inline-css";
  s.textContent = `
    .p-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .p-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .role-chip{ display:flex; align-items:center; gap:10px; border:1px solid #e5e7eb; border-radius:12px; padding:10px; }
    .role-chip:hover{ background:#f9fbff; }
    .rank-input{ width:100px; }
    .icon-btn{ border:0; background:transparent; cursor:pointer; }
    .soft{ color:#6b7280; }
  `;
  document.head.appendChild(s);
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

export default function EditTeamRolePosition() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  const [deptList, setDeptList] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState("");
  const [selectedDeptObj, setSelectedDeptObj] = useState(null);

  const [changedDept, setChangedDept] = useState(null);
  const [deletedRoles, setDeletedRoles] = useState([]);

  useEffect(() => injectOnce(), []);

  // Accept departmentId via state OR query (for preselect), but ALWAYS editable
  const viaState = location?.state || {};
  const depFromState = viaState?.departmentId || "";
  const params = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : "",
  );
  const depFromQuery = params.get("departmentId") || "";
  const presetDeptId = depFromState || depFromQuery;

  const load = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const res = await _Get(ViewDepartmentWise_Assign_Role, user?.database);
      const arr = Array.isArray(res?.Department) ? res.Department : [];
      setDeptList(arr);

      if (presetDeptId) {
        const found = arr.find(
          (d) =>
            d?._id === presetDeptId || d?.departmentName?._id === presetDeptId,
        );
        if (found) {
          setSelectedDeptId(found?._id);
          setSelectedDeptObj(found);
          setChangedDept(found);
          setDeletedRoles([]);
        } else {
          setSelectedDeptId("");
          setSelectedDeptObj(null);
          setChangedDept(null);
          setDeletedRoles([]);
          swal("Department not found");
        }
      } else {
        setSelectedDeptId("");
        setSelectedDeptObj(null);
        setChangedDept(null);
        setDeletedRoles([]);
      }
    } catch (err) {
      console.error("[EditTeamRolePosition] load error:", err);
      swal("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); /* eslint-disable-line */
  }, []);

  const totalRoles = useMemo(
    () => selectedDeptObj?.roles?.length || 0,
    [selectedDeptObj],
  );

  const onChangeDept = (id) => {
    setSelectedDeptId(id);
    const found = deptList.find((d) => d?._id === id);
    setSelectedDeptObj(found || null);
    setChangedDept(found || null);
    setDeletedRoles([]);
  };

  const updateRank = (idx, newRank) => {
    const val = Number(newRank);
    const deptCopy = { ...(changedDept || selectedDeptObj) };
    const list = Array.isArray(deptCopy?.roles) ? [...deptCopy.roles] : [];
    if (!list[idx]) return;
    list[idx] = { ...list[idx], rolePosition: val };
    deptCopy.roles = list;

    setChangedDept(deptCopy);
    setSelectedDeptObj(deptCopy);
    setDeptList((prev) =>
      prev.map((d) => (d?._id === deptCopy?._id ? deptCopy : d)),
    );
  };

  const deleteRole = (roleObj) => {
    swal("Warning", "Sure You Want to Delete Role", {
      buttons: { catch: { text: "Delete", value: "delete" }, cancel: "Cancel" },
    }).then((value) => {
      if (value !== "delete") return;

      const deptCopy = { ...(changedDept || selectedDeptObj) };
      const list = Array.isArray(deptCopy?.roles) ? [...deptCopy.roles] : [];
      const idx = list.indexOf(roleObj);
      if (idx >= 0) list.splice(idx, 1);
      deptCopy.roles = list;

      setDeletedRoles((prev) => [...prev, roleObj]);
      setChangedDept(deptCopy);
      setSelectedDeptObj(deptCopy);
      setDeptList((prev) =>
        prev.map((d) => (d?._id === deptCopy?._id ? deptCopy : d)),
      );
    });
  };

  const saveChanges = async () => {
    if (!changedDept?._id) return swal("Please select a department");

    const currentRoles = (changedDept?.roles || []).map((val) => ({
      database: val?.database,
      roleName: val?.roleName,
      roleId: val?.roleId?._id || val?.roleId || val?._id,
      rolePosition: val?.rolePosition,
    }));

    const removed = (deletedRoles || []).map((val) => ({
      database: val?.database,
      roleName: val?.roleName,
      roleId: val?.roleId?._id || val?.roleId || val?._id,
      rolePosition: val?.rolePosition,
    }));

    try {
      setLoading(true);
      const res = await _Put(
        Update_AssignRole_InDepartment1,
        changedDept?._id,
        {
          roles: currentRoles,
          deleteRole: removed,
        },
      );
      if (res) {
        swal("Roles Changed Successfully");
        await load();
      }
    } catch (err) {
      console.error(err);
      swal("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* ===== Toolbar ===== */}
      <Row className="p-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="p-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Edit Department Hierarchy{" "}
                <span className="count-pill">{totalRoles}</span>
              </div>
              <div style={{ flex: "1 1 240px" }} />
              <Button
                style={{
                  backgroundColor: "rgb(8, 91, 245)",
                  color: "white",
                  fontWeight: 600,
                  height: 32,
                }}
                onClick={() => navigate(-1)}
              >
                Back
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Selector ===== */}
      <Row>
        <Col sm="12">
          <Card className="p-card" style={{ padding: 14, marginBottom: 12 }}>
            <Row style={{ gap: 12 }}>
              <Col lg="4" md="6" sm="12">
                <Label>Select Department</Label>
                <Input
                  type="select"
                  value={selectedDeptId}
                  onChange={(e) => onChangeDept(e.target.value)}
                >
                  <option value="">-- Select Department --</option>
                  {deptList.map((d) => (
                    <option key={d?._id} value={d?._id}>
                      {d?.departmentName?.departmentName ||
                        d?.departmentName ||
                        "-"}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col
                lg="8"
                md="6"
                sm="12"
                style={{ display: "flex", alignItems: "flex-end", gap: 8 }}
              >
                <Button
                  color="primary"
                  onClick={saveChanges}
                  disabled={!selectedDeptObj}
                >
                  Save Changes
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* ===== Roles (3 columns) ===== */}
      {!selectedDeptObj ? (
        <Row>
          <Col sm="12">
            <Card
              className="p-card"
              style={{ padding: 16, textAlign: "center" }}
            >
              Select a department to view roles.
            </Card>
          </Col>
        </Row>
      ) : (
        <Row>
          {(selectedDeptObj?.roles || []).length === 0 ? (
            <Col sm="12">
              <Card
                className="p-card"
                style={{ padding: 16, textAlign: "center" }}
              >
                No roles assigned.
              </Card>
            </Col>
          ) : (
            selectedDeptObj.roles.map((role, idx) => {
              const rid = role?.roleId?._id || role?.roleId || role?._id || idx;
              const roleName = role?.roleName || "-";
              const rank = toStr(role?.rolePosition ?? "");
              return (
                <Col
                  key={rid}
                  lg="4"
                  md="6"
                  sm="12"
                  style={{ marginBottom: 12 }}
                >
                  <Card className="p-card" style={{ padding: 12 }}>
                    <div className="role-chip">
                      <button
                        className="icon-btn"
                        title="Remove role from department"
                        onClick={() => deleteRole(role)}
                      >
                        <span
                          style={{
                            fontSize: 18,
                            color: "#ef4444",
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </span>
                      </button>
                      <div style={{ flex: 1, fontWeight: 700 }}>
                        {roleName.length > 26
                          ? `${roleName.slice(0, 26)}…`
                          : roleName}
                      </div>
                      <Input
                        className="rank-input"
                        type="number"
                        placeholder="Rank"
                        value={rank}
                        onChange={(e) => updateRank(idx, e.target.value)}
                      />
                    </div>
                  </Card>
                </Col>
              );
            })
          )}
        </Row>
      )}
    </>
  );
}
